/**
 * Crew visibility — who else is on the same job the same day.
 * `crew` comes from GET /api/dispatches (`crew: [{tech_id, name, color, hours}]`).
 */
import { Users, History } from "lucide-react";
import { useI18n } from "@/i18n";

const short = (name = "") => {
  const [first, ...rest] = name.trim().split(/\s+/);
  return rest.length ? `${first} ${rest[0][0]}.` : first;
};

export function CrewLine({ crew, className = "", testId }) {
  const { t } = useI18n();
  if (!crew?.length) return null;
  return (
    <div className={`flex items-center gap-1 flex-wrap text-[8px] md:text-[10px] text-slate-700 mt-0.5 ${className}`} data-testid={testId} title={`Also on this job today: ${crew.map(c => c.name).join(", ")}`}>
      <Users className="w-2.5 h-2.5 text-slate-500 shrink-0" />
      <span className="font-semibold text-slate-500">{t("Working with:")}</span>
      {crew.map((c) => (
        <span key={c.tech_id || c.name} className="inline-flex items-center gap-0.5 font-semibold" data-testid={testId ? `${testId}-${c.tech_id}` : undefined}>
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c.color || "#64748b" }} />
          {short(c.name)}{c.hours != null ? <span className="text-slate-400 font-normal">({c.hours}h)</span> : null}
        </span>
      ))}
    </div>
  );
}

export function CrewBadge({ crew, testId }) {
  if (!crew?.length) return null;
  return (
    <span className="inline-flex items-center gap-0.5 text-[7px] font-bold text-sky-800 bg-sky-100 border border-sky-200 px-1 py-0 rounded shrink-0"
      title={`Also on this job today: ${crew.map(c => c.name).join(", ")}`} data-testid={testId}>
      <Users className="w-2 h-2" />+{crew.length}
    </span>
  );
}

const mdy = (iso) => { if (!iso) return ""; const [y, m, d] = iso.split("-"); return `${Number(m)}/${Number(d)}/${y.slice(2)}`; };

/** Techs who have been to this site before (any job) — so the tech knows who to call with a question. */
export function PriorTechsLine({ techs, className = "", testId }) {
  const { t } = useI18n();
  if (!techs?.length) return null;
  return (
    <div className={`flex items-center gap-1 flex-wrap text-[8px] md:text-[10px] text-slate-500 mt-0.5 ${className}`} data-testid={testId}
      title={`Been to this site before: ${techs.map(t => `${t.name} (${mdy(t.last_date)})`).join(", ")}`}>
      <History className="w-2.5 h-2.5 text-slate-400 shrink-0" />
      <span className="font-semibold">{t("Prior techs:")}</span>
      {techs.map((t) => (
        <span key={t.tech_id} className="inline-flex items-center gap-0.5" data-testid={testId ? `${testId}-${t.tech_id}` : undefined}>
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.color || "#64748b" }} />
          {short(t.name)}<span className="text-slate-400 num">({mdy(t.last_date)})</span>
        </span>
      ))}
    </div>
  );
}
